import React from 'react';
import { Link, generatePath } from 'react-router-dom';
import { Collapse, Typography, List, Badge, Tag, Skeleton } from 'antd';
import {
  CaretRightOutlined,
  CheckCircleFilled,
  LikeTwoTone,
  DislikeTwoTone
} from '@ant-design/icons';
import { ProblemResponse, ProblemSetNestedResponse } from '../../api/problems';
import { DIFFICULTY_MAP, NEW_PROBLEM_DAYS, PROBLEM_ROUTE } from '../../config/constants';
import { sortProblemSet } from '../../utils/problems';
import styles from './ProblemSetsCollapse.module.css';

// check if a given problem is considered "new"
const isNewProblem = (problem: ProblemResponse): boolean => {
  const dateAdded = new Date(problem.date_added);
  // get last date when a problem is considered "new"
  const lastNewProblemDate = new Date();
  lastNewProblemDate.setDate(lastNewProblemDate.getDate() - NEW_PROBLEM_DAYS);

  return dateAdded > lastNewProblemDate;
};

// problem filter object
export interface ProblemFilter {
  // allow string indexing
  [key: string]: any;
  Beginner: boolean
  Intermediate: boolean
  Advanced: boolean
};

export interface ProblemSetsCollapseProps {
  problemSets: ProblemSetNestedResponse[]
  filter: ProblemFilter
  expanded: string[]
  onChange?: (keys: string[]) => void
};

// shows list of problem sets and problems
// in a collapsible component
const ProblemSetsCollapse = (props: ProblemSetsCollapseProps): JSX.Element | null => {
  return (
    <div className={styles.mainContainer}>
      {props.problemSets.length > 0
        ? <Collapse
            bordered={false}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            activeKey={props.expanded}
            onChange={key => props.onChange ? props.onChange(Array.isArray(key) ? key : [key]) : null}
            className={`site-collapse-custom-collapse ${styles.mainCollapse}`}
          >
            {[...props.problemSets].filter((ps: ProblemSetNestedResponse) => ps.problems.filter(
              (p: ProblemResponse) => props.filter[DIFFICULTY_MAP[p.difficulty].name]).length > 0
            ).sort(
                (ps1: ProblemSetNestedResponse, ps2: ProblemSetNestedResponse) => (ps1.title > ps2.title) ? 1 : -1
            ).map((ps: ProblemSetNestedResponse, psIndex: number) =>
              <Collapse.Panel
                header={
                  <div className={styles.problemSetPanelHeader}>
                    <Typography.Text
                      className={styles.problemSetPanelHeaderText}
                      strong={true}
                    >
                      {ps.title}
                    </Typography.Text>
                    {ps.icon !== null && <img
                      className={styles.problemSetPanelHeaderIcon}
                      alt={`${ps.title} Icon`}
                      src={ps.icon}
                    />}
                  </div>
                }
                key={ps.id.toString()}
                className={`site-collapse-custom-panel ${psIndex === 0 ? styles.problemSetPanelItemFirstChild : styles.problemSetPanelItem}`}
              >
                <List
                  size='small'
                  bordered={true}
                  dataSource={sortProblemSet(ps.problems).filter((p: ProblemResponse) => props.filter[DIFFICULTY_MAP[p.difficulty].name])}
                  className={styles.problemList}
                  locale={{ emptyText: <></> }}
                  renderItem={(p: ProblemResponse, pIndex: number) => (
                    <Link to={generatePath(PROBLEM_ROUTE, { key: p.slug ? p.slug : p.id.toString() })}>
                      <Badge.Ribbon
                        text={<CheckCircleFilled className={'badgeRibbonCheckIcon'}/>}
                        className={p.is_solved ? 'badgeRibbonShowLight' : 'badgeRibbonHide'}
                      >
                        <List.Item
                          key={p.id}
                          className={`${styles.problemListItem} ${pIndex === 0 ? styles.problemListItemFirstChild : ''}`}
                        >
                          <Badge
                            count={'NEW'}
                            color={'#FF5C33'}
                            className={isNewProblem(p) ? styles.problemNewBadge : styles.problemNewBadgeHide}
                          >
                          </Badge>
                          <div className={styles.problem}>
                            <Typography.Text
                              className={styles.problemTitleText}
                              strong={true}
                            >
                              {p.title}
                            </Typography.Text>
                            &nbsp;&nbsp;
                            <Tag className={DIFFICULTY_MAP[p.difficulty].tagClass}>
                              {DIFFICULTY_MAP[p.difficulty].name}
                            </Tag>
                            <Tag className={styles.problemSolvedCountTag}>
                              {p.solved_count} solvers
                            </Tag>
                            <div className={styles.descriptionLikesContainer}>
                            <div className={styles.descriptionLikePairContainer}>
                              <div className={styles.descriptionLikeContainer}>
                                <LikeTwoTone twoToneColor='#182A52' />
                              </div>
                              <div className={styles.descriptionLikeCountContainer}>
                                <Typography.Text className={styles.descriptionLikeCount}>
                                  {Intl.NumberFormat('en-US', {
                                    notation: 'compact',
                                    maximumFractionDigits: 1
                                  }).format(p.likes)}
                                </Typography.Text>
                              </div>
                            </div>
                            <div className={styles.descriptionLikePairContainer}>
                              <div className={styles.descriptionLikeContainer}>
                                <DislikeTwoTone twoToneColor='#182A52' />
                              </div>
                              <div className={styles.descriptionLikeCountContainer}>
                                <Typography.Text className={styles.descriptionLikeCount}>
                                  {Intl.NumberFormat('en-US', {
                                    notation: 'compact',
                                    maximumFractionDigits: 1
                                  }).format(p.dislikes)}
                                </Typography.Text>
                              </div>
                            </div>
                          </div>
                          </div>
                        </List.Item>
                      </Badge.Ribbon>
                    </Link>
                  )}
                />
              </Collapse.Panel>
            )}
          </Collapse>
          :
          <div>
            <Skeleton active={true} paragraph={{rows: 4}} />
            <Skeleton active={true} paragraph={{rows: 4}} />
          </div>}
    </div>
  );
};

export default ProblemSetsCollapse;
